import { Match } from '../models/analytics-api/search_result.model';
import { IQueryFilter } from '../interfaces/query-filter.interface';
import { Dimension, DimensionProperty } from './analytics-api/schema.model';
import { hasValue } from '../utils/filter.util';
import { CHIP_FILTER_OPERATORS } from '../constants/query-filter.constants';

export class QueryFilter {
  public dataset?: string;
  public displayName?: string;
  public type: string;
  public key: string;
  public values: string[] | Match[] | string;
  public dimension: Dimension = undefined;
  public dimensionProperty: DimensionProperty = undefined;
  public isSearchable?: boolean;

  constructor(
    object: IQueryFilter
  ) {
    this.type = object.type;
    this.values = object.values || [];
    if (CHIP_FILTER_OPERATORS.includes(this.type) && typeof this.values === 'string') {
      this.values = this.values ? [this.values] : [];
    }

    if (!!object.dimension) {
      const properties = object.dimension.properties?.map(dp => new DimensionProperty(dp.name, dp.meta, dp.isSearchable, dp.dimensionName));
      this.dimension = new Dimension(object.dimension.name, object.dimension.meta, properties, object.dimension.isSearchable);
    }

    if (!!object.dimension_property) {
      this.dimensionProperty = new DimensionProperty(object.dimension_property.name, object.dimension_property.meta, object.dimension_property.isSearchable, object.dimension?.name);
    }

    if (object.dataset) {
      this.dataset = object.dataset;
    }

    if (object.is_searchable) {
      this.isSearchable = object.is_searchable;
    }

    this.key = this.dimension ? this.combinedKey : object.key;
  }

  public get isChipFilter(): boolean {
    return CHIP_FILTER_OPERATORS.includes(this.type) && Array.isArray(this.values);
  }

  public get matchValues(): string[] {
    if (!Array.isArray(this.values)) return [];
    return (this.values as string[]).filter(Boolean);
  }

  analyticsPayload() {
    if (this.type === 'na' || this.type === 'not_na') {
      return {
        type: this.type,
        key: this.analyticsApiKey,
        values: [],
      };
    }
    if (this.isChipFilter) {
      const key = this.analyticsApiKey;
      const values = this.matchValues;
      if (!values.length) {
        return null;
      }
      if (values.length === 1) {
        return { type: this.type, key, values };
      }
      return {
        type: 'or',
        expressions: values.map(v => ({ type: this.type, key, values: [v] })),
      };
    }
    if ((this.type === 'any' || this.type === 'not_any') && !this.matchCount) {
      return null;
    }
    const values = Array.isArray(this.values) ? this.values : [this.values];
    if (!values.some(value => hasValue(value))) {
      return null;
    }
    return {
      type: this.type,
      key: this.analyticsApiKey,
      values,
    };
  }

  private get matchCount(): number {
    return Array.isArray(this.values) ? this.values.length : Number(!!this.values);
  }

  private get analyticsApiKey(): string {
    switch (this.type) {
      case 'any':
      case 'not_any':
        return this.dimension?.name || this.key;
      case 'na':
      case 'not_na':
        if (this.dimensionProperty) {
          return this.key;
        } else {
          return this.dimension?.name || this.key;
        }
      default:
        if (this.dimension) {
          return this.combinedKey;
        } else {
          return `${this.key}.${this.key}`;
        }
    }
  }

  private get isDimensionProperty() {
    return !!this.dimensionProperty;
  }

  private get combinedKey() {
    if (this.isDimensionProperty) {
      return `${this.dimension.name}.${this.dimensionProperty.name}`;
    } else {
      return `${this.dimension.name}.${this.dimension.name}`;
    }
  }

  asJson() {
    return {
      type: this.type,
      values: this.values,
      is_searchable: this.isSearchable,
      dimension: this.dimension && { name: this.dimension.name },
      dimension_property: this.dimensionProperty && { name: this.dimensionProperty.name },
      key: this.key,
    }
  }

}
